import React, { ComponentProps, useState, useEffect } from 'react';
import {
  asTrackingPayload,
  asWidgetProps,
  BucketFormikForm,
  BucketFormikInput,
  BucketFormikInputProps,
} from 'Src/types/widget-helpers';
import { useDispatch } from 'react-redux';
import { isEmpty, get } from 'lodash';
import { Formik } from 'formik';
import { useAppContext } from 'Context/ReactAppContext';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { t } from 'Src/i18n';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { ID_MODAL_HEADER__PLE__NEWSLETTER } from 'Components/utils/identifiers/constants';
import { ICONS } from 'Components/utils/icon/icons';
import { COLORS } from 'Components/utils/color/colors';
import { onCheckboxChange } from 'Components/widgets/profile-info/newsletter-subscription/NewsletterSubscription.utils';
import {
  formatDataForSubscription,
  submitSubscriptionForm,
} from 'Views/newsletter/newsletterActions';
import ratios from 'Components/utils/ratios/ratios.utils';
import { Button } from 'Components/frameworks/button/Button';
import { FormErrors } from 'Components/frameworks/form-errors/FormErrors';
import Image from 'Components/frameworks/image/Image';
import { Modal } from 'Components/frameworks/modal/Modal';
import { Text } from 'Components/frameworks/text/Text';
import { Title } from 'Components/frameworks/title/Title';
import {
  NewsletterForm,
  subscriptionSchema,
} from 'Components/widgets/newsletter-form/NewsletterForm';
import { SUBSCRIPTION_ORIGIN } from 'Views/booking/constants';
import { FormFeedback } from 'Components/frameworks/form-feedback/FormFeedback';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
export const POPIN_NEWSLETTER = 'POPIN_NEWSLETTER';
type Props = {
  checkboxes: Array<{
    description: string;
    id: string;
  }>;
  legalInformations: Array<{
    description: string;
    label: string;
    id: number;
    required: boolean;
  }>;
  onClose: () => void;
};
export const NEWSLETTER_SUBSCRIPTION_VALIDATION = 'NEWSLETTER_SUBSCRIPTION_VALIDATION';
export function PopinNewsletter({ checkboxes = [], legalInformations = [], onClose }: Props) {
  const appContext = useAppContext();
  const defaultPayload = {
    categoryGA: 'newsletter',
    actionGA: 'shopping',
  };
  const track = useTracking(defaultPayload);
  const [submitErrors, setSubmitErrors] = useState([]);
  const dispatch = useDispatch();
  const { isTablet } = useResponsive();
  const labelConfirmation = `${t('newsletter.confirmation.headline')} \n ${t('newsletter.confirmation.description')}`;
  useEffect(
    () =>
      track({
        labelGA: 'displayPopin',
        nonInteraction: 1,
      }),
    [],
  );
  const displayPhoneInput = isFeatureFlipped(appContext)('newsletter.displayPhoneInput');
  const displayEmailInput = isFeatureFlipped(appContext)('newsletter.displayEmailInput');
  return (
    <Modal
      {...asWidgetProps<ComponentProps<typeof Modal>>({
        ariaLabel: t('newsletter.popin.title'),
        ariaLabelledby: ID_MODAL_HEADER__PLE__NEWSLETTER,
        closeLabel: t('common.close'),
        onClose: onClose,
        size: 'large',
      })}
    >
      <div className="sm:flex">
        <div className="relative w-full flex-none sm:w-12/25 md:w-1/3">
          <Image
            {...asWidgetProps<ComponentProps<typeof Image>>({
              className: 'sm:absolute sm:inset-0',
              dimensions: isTablet
                ? {
                    w: 320,
                    h: 700,
                  }
                : {
                    w: 500,
                    h: 200,
                  },
              imageUrl: t('newsletter.popin.imageUrl'),
              ratioDisplay: 1 / 2,
              ratioKey: ratios.XS,
            })}
          />
        </div>
        <div className="m-25">
          <Title
            {...asWidgetProps<ComponentProps<typeof Title>>({
              iconColor: COLORS.SIENNA,
              iconName: ICONS.MAIL,
              iconSize: '1.5rem',
              id: ID_MODAL_HEADER__PLE__NEWSLETTER,
              theme: 'title-brand-n2',
            })}
          >
            {t('newsletter.popin.title')}
          </Title>
          <Text
            {...asWidgetProps<ComponentProps<typeof Text>>({
              className: 'my-10',
              text: t('newsletter.popin.subtitle'),
            })}
          />
          <Formik
            {...asWidgetProps<ComponentProps<typeof Formik>>({
              initialValues: {
                civility: 'M',
                ...checkboxes.reduce((newslettersCheckbox, newsletter) => {
                  newslettersCheckbox[newsletter.id] = isFeatureFlipped(appContext)(
                    'newsletter.preCheckedNewsletterPopin',
                  );
                  return newslettersCheckbox;
                }, {}),
              },
              onSubmit: async (values) => {
                const response = await submitSubscriptionForm({
                  appContext,
                  data: formatDataForSubscription({
                    ...values,
                    originCode: SUBSCRIPTION_ORIGIN.POPIN,
                  }),
                  isSubscription: true,
                });
                track(
                  asTrackingPayload({
                    labelGA: 'registrationPopin',
                    user: {
                      neolaneID: get(response, 'subscription.neolaneId'),
                    },
                  }),
                );
                const error = get(response, 'subscription.error.errorDescription', '');
                if (!isEmpty(error)) {
                  return setSubmitErrors([error]);
                }
                dispatch({
                  payload: {
                    toastLabel: labelConfirmation,
                  },
                  type: NEWSLETTER_SUBSCRIPTION_VALIDATION,
                });
                onClose && onClose();
              },
              validationSchema: subscriptionSchema(
                displayEmailInput,
                displayPhoneInput,
                appContext.locale,
              ),
            })}
          >
            {({ setFieldValue, isValid, touched, values }) => (
              <BucketFormikForm>
                <NewsletterForm />
                {checkboxes.map((checkbox) => (
                  <div className="mt-15" key={checkbox.id}>
                    <BucketFormikInput
                      {...asWidgetProps<BucketFormikInputProps>({
                        label: checkbox.description,
                        name: checkbox.id,
                        onChange: (e) => onCheckboxChange(e.target, setFieldValue),
                        type: 'checkbox',
                        value: checkbox.id,
                      })}
                    />
                  </div>
                ))}
                {['en-CN', 'zh-CN'].includes(appContext.locale) && (
                  <div className="mb-15">
                    <BucketFormikInput
                      {...asWidgetProps<BucketFormikInputProps>({
                        label: (
                          <Text
                            {...asWidgetProps<ComponentProps<typeof Text>>({
                              text: legalInformations.find(
                                ({ id }) => String(id) === 'PRIVACY_POLICY',
                              )?.label,
                            })}
                          />
                        ),
                        name: 'privacyPolicyAggreement',
                        type: 'checkbox',
                      })}
                    />
                    {touched.privacyPolicyAggreement && !values.privacyPolicyAggreement && (
                      <FormFeedback
                        {...asWidgetProps<ComponentProps<typeof FormFeedback>>({
                          key: 'd',
                          isError: true,
                          message: t('newsletter.errorMessages.privacyPolicyNotAccepted'),
                        })}
                      />
                    )}
                  </div>
                )}
                <div className="mt-15 text-right">
                  {!isEmpty(submitErrors) && (
                    <FormErrors
                      {...asWidgetProps<ComponentProps<typeof FormErrors>>({
                        texts: submitErrors,
                      })}
                    />
                  )}
                  <Button
                    {...asWidgetProps<ComponentProps<typeof Button>>({
                      isDisabled:
                        !isValid ||
                        !values.CLUBMED ||
                        (['en-CN', 'zh-CN'].includes(appContext.locale) &&
                          !values.privacyPolicyAggreement),
                      type: 'submit',
                    })}
                  >
                    {t('newsletter.popin.cta')}
                  </Button>
                </div>
              </BucketFormikForm>
            )}
          </Formik>
          <div className="mt-15">
            {legalInformations.map((info) => {
              return !isEmpty(info.description) ? (
                <Text
                  {...asWidgetProps<ComponentProps<typeof Text>>({
                    key: info.id,
                    text: info.description,
                    theme: 'text-sans-n2',
                  })}
                />
              ) : null;
            })}
          </div>
        </div>
      </div>
    </Modal>
  );
}
